import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { api } from "../App";
import { format } from "date-fns";
import { useWebSocket } from "./useWebSocket";

const ReceptionAlertContext = createContext();

// Simple notification sound using Web Audio API
const createNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    return () => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(600, audioContext.currentTime + 0.1);
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
      
      setTimeout(() => {
        const oscillator2 = audioContext.createOscillator();
        const gainNode2 = audioContext.createGain();
        
        oscillator2.connect(gainNode2);
        gainNode2.connect(audioContext.destination);
        
        oscillator2.type = 'sine';
        oscillator2.frequency.setValueAtTime(600, audioContext.currentTime);
        oscillator2.frequency.exponentialRampToValueAtTime(480, audioContext.currentTime + 0.1);
        
        gainNode2.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
        
        oscillator2.start(audioContext.currentTime);
        oscillator2.stop(audioContext.currentTime + 0.5);
      }, 150);
    };
  } catch (error) {
    console.error("Error creating audio context:", error);
    return () => {};
  }
};

export function ReceptionAlertProvider({ children }) {
  const [patientsCount, setPatientsCount] = useState(0);
  const previousCount = useRef(0);
  const playSound = useRef(createNotificationSound());
  
  // Usar useRef para evitar re-criação da função
  const checkReceptionRef = useRef(null);

  const checkReception = useCallback(async () => {
    // Only check if user is authenticated
    const token = localStorage.getItem("token");
    if (!token) {
      return;
    }
    
    try {
      const res = await api.get("/appointments");
      const today = format(new Date(), "yyyy-MM-dd");
      const inReception = res.data.filter(
        (apt) => apt.status === "na_recepcao" && apt.date === today
      );
      const newCount = inReception.length;
      
      // Play sound if count increased (new patient arrived)
      if (newCount > previousCount.current && previousCount.current > 0) {
        try {
          playSound.current();
        } catch (err) {
          console.error("Error playing sound:", err);
        }
      }
      
      previousCount.current = newCount;
      setPatientsCount(newCount);
    } catch (err) {
      // Silently fail - user might not be on appointments page
      console.debug("Reception check skipped:", err.message);
    }
  }, []);
  
  // Atualizar ref
  useEffect(() => {
    checkReceptionRef.current = checkReception;
  }, [checkReception]);
  
  // WebSocket handler - atualiza em tempo real
  const handleWebSocketMessage = useCallback((message) => {
    console.log('[useReceptionAlert] WebSocket message:', message);
    
    if (message.type === 'appointment_created' || 
        message.type === 'appointment_updated' || 
        message.type === 'appointment_deleted' ||
        message.type === 'waiting_list_update') {
      console.log('[useReceptionAlert] Atualizando contagem via WebSocket');
      checkReceptionRef.current?.();
    }
  }, []);
  
  // Conectar WebSocket
  const { isConnected } = useWebSocket({
    onMessage: handleWebSocketMessage,
    enabled: true
  });
  
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      // Check inicial
      checkReception();
      
      // Fallback: polling a cada 30 segundos caso WebSocket falhe
      const interval = setInterval(checkReception, 30000);
      return () => clearInterval(interval);
    }
  }, [checkReception]);

  return (
    <ReceptionAlertContext.Provider value={{ patientsCount, isConnected }}>
      {children}
    </ReceptionAlertContext.Provider>
  );
}

export function useReceptionAlert() {
  const context = useContext(ReceptionAlertContext);
  if (!context) {
    throw new Error("useReceptionAlert must be used within ReceptionAlertProvider");
  }
  return context;
}
